"use client";

import type { ReactNode, RefObject } from "react";
import { ChevronDown, Search, X } from "@/components/icons";

import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type {
  InlineComboboxController,
  ResolvedInlineComboboxProps,
} from "./use-inline-combobox";

export function scopedLabel(
  action: string,
  subject?: string,
  fallback = action,
) {
  return subject ? `${action} ${subject}` : fallback;
}

interface InlineComboboxFieldProps {
  props: ResolvedInlineComboboxProps;
  combobox: InlineComboboxController;
  inputRef: RefObject<HTMLInputElement | null>;
  customContent: ReactNode;
}

function inputPlaceholder(
  props: ResolvedInlineComboboxProps,
  open: boolean,
  showCustomValue: boolean,
) {
  if (showCustomValue) return undefined;
  return open
    ? (props.searchPlaceholder ?? props.placeholder)
    : props.placeholder;
}

function InlineComboboxInput({
  props,
  combobox,
  inputRef,
  showCustomValue,
  showClear,
}: Omit<InlineComboboxFieldProps, "customContent"> & {
  showCustomValue: boolean;
  showClear: boolean;
}) {
  const { open, listId, activeValue } = combobox;
  return (
    <Input
      ref={inputRef}
      id={props.id}
      role="combobox"
      aria-label={props.ariaLabel}
      aria-invalid={props.ariaInvalid}
      aria-describedby={props.ariaDescribedby}
      aria-required={props.ariaRequired}
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-activedescendant={
        open && activeValue ? `${listId}-${activeValue}` : undefined
      }
      disabled={props.disabled}
      size={props.size}
      variant={props.variant === "filled" || props.variant === "ghost" ? props.variant : "default"}
      readOnly={showCustomValue || !open}
      value={showCustomValue ? "" : open ? combobox.query : combobox.closedText}
      placeholder={inputPlaceholder(props, open, showCustomValue)}
      className={cn(
        !open && "cursor-pointer",
        props.className,
        open || props.leadingIcon ? "pl-9" : props.variant === "ghost" ? "pl-2" : "pl-3",
        showClear ? "pr-14" : "pr-8",
      )}
      onClick={combobox.openAndSeed}
      onChange={(event) => combobox.setQuery(event.target.value)}
      onKeyDown={combobox.handleKeyDown}
    />
  );
}

function InlineComboboxLeadingIcon({
  open,
  leadingIcon,
}: {
  open: boolean;
  leadingIcon: ReactNode;
}) {
  if (open) {
    return (
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
    );
  }
  if (!leadingIcon) return null;
  return (
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
      {leadingIcon}
    </span>
  );
}

function InlineComboboxTrailing({
  showClear,
  clearLabel,
  onClear,
}: {
  showClear: boolean;
  clearLabel: string;
  onClear: () => void;
}) {
  return (
    <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
      {showClear ? (
        <IconButton
          size="inline"
          aria-label={clearLabel}
          className="pointer-events-auto text-muted-foreground"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClear}
        >
          <X />
        </IconButton>
      ) : null}
      <ChevronDown
        aria-hidden
        className="pointer-events-none h-4 w-4 opacity-50"
      />
    </div>
  );
}

function shouldShowClear(
  props: ResolvedInlineComboboxProps,
  open: boolean,
  query: string,
) {
  if (props.disabled) return false;
  return open ? query.length > 0 : Boolean(props.value && props.onClear);
}

export function InlineComboboxField({
  props,
  combobox,
  inputRef,
  customContent,
}: InlineComboboxFieldProps) {
  const { open, query, setQuery } = combobox;
  const showCustomValue = customContent != null;
  const showClear = shouldShowClear(props, open, query);
  const clear = () => {
    if (open) {
      setQuery("");
      inputRef.current?.focus();
    } else {
      props.onClear?.();
    }
  };

  return (
    <>
      <InlineComboboxLeadingIcon open={open} leadingIcon={props.leadingIcon} />
      <InlineComboboxInput
        props={props}
        combobox={combobox}
        inputRef={inputRef}
        showCustomValue={showCustomValue}
        showClear={showClear}
      />
      {showCustomValue ? (
        <span
          className={cn(
            "pointer-events-none absolute inset-y-0 flex items-center text-sm",
            props.leadingIcon ? "left-9" : "left-3",
            "right-8",
          )}
        >
          {customContent}
        </span>
      ) : null}
      <InlineComboboxTrailing
        showClear={showClear}
        clearLabel={scopedLabel("Clear", props.ariaLabel)}
        onClear={clear}
      />
    </>
  );
}
